/**
 * API клиент для работы с FastAPI backend v2.0
 * 
 * Поддерживает:
 * - JWT аутентификацию (Bearer tokens)
 * - Автоматическое добавление токена к запросам
 * - Обработку 401 ошибок (auto logout)
 * - Типизацию запросов и ответов
 */

import { ApiClientError, messageFromFastApiDetail } from "@/lib/geometry-api-errors";
import { screenLockGate } from "@/lib/screen-lock-gate";

// Используем относительный путь для проксирования через Vite
// Vite проксирует /api/v1/* к бэкенду (настроено в vite.config.ts)
//
// Если VITE_API_URL установлен:
//   - и это полный URL (http://...) → используем его как базу и гарантируем, что в конце есть /api/v1
//   - и это относительный путь → нормализуем к '/api/v1'
// Если VITE_API_URL не задан → по умолчанию '/api/v1' (через Vite proxy)
const envApiUrl = import.meta.env.VITE_API_URL;

function resolveApiBaseUrl(raw?: string): string {
  // База по умолчанию — относительный путь, который проксируется Vite
  let base = raw && raw.length > 0 ? raw : '/api/v1';

  // Убираем завершающий слэш для единообразия
  if (base.endsWith('/')) {
    base = base.slice(0, -1);
  }

  // Если это полный URL (http:// или https://) и он не заканчивается на /api/v1 — добавляем префикс API
  if (base.startsWith('http') && !base.endsWith('/api/v1')) {
    base = `${base}/api/v1`;
  }

  // Если это относительный путь и он не /api/v1 — нормализуем к /api/v1
  if (!base.startsWith('http') && base !== '/api/v1') {
    base = '/api/v1';
  }

  return base;
}

const _resolved = resolveApiBaseUrl(envApiUrl);

/**
 * В Vite dev при `VITE_API_URL=http://localhost:8000/...` браузер бьёт напрямую в :8000 (обход proxy).
 * Клиент :5173 → CORS, другой инстанс бэка, расхождение с Docker-именем. MVT и т.п. идут на `origin` + `/api/v1` (см. vite.config proxy) — унифицируем.
 */
function effectiveApiBaseInBrowser(resolved: string): string {
  if (typeof window === "undefined") {
    return resolved;
  }
  if (!import.meta.env.DEV) {
    return resolved;
  }
  if (/^https?:\/\/(localhost|127\.0\.0\.1):8000\/api\/v1$/i.test(resolved)) {
    return "/api/v1";
  }
  return resolved;
}

/**
 * Единая база API для фронтенда (нормализована с учётом Vite proxy и dev-режима).
 * Экспортируется, чтобы XHR-загрузки файлов ходили по той же базе, что и остальные
 * запросы, — иначе прямой `VITE_API_URL` ломает аплоад при открытии не с localhost.
 */
export const API_BASE_URL = effectiveApiBaseInBrowser(_resolved);
const TOKEN_KEY = "access_token";

/**
 * Интерфейс для API ошибок
 */
export interface ApiError {
  detail: string | Array<{
    loc: string[];
    msg: string;
    type: string;
  }>;
}

/**
 * Получение токена из localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Сохранение токена в localStorage
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Удаление токена из localStorage
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Проверка наличия токена
 */
export function hasToken(): boolean {
  return !!getToken();
}

/**
 * Создание заголовков для запроса
 */
function createHeaders(includeAuth: boolean = true): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const unlock = screenLockGate.getUnlockToken();
    if (unlock) {
      headers["X-Screen-Unlock"] = unlock;
    }
  }

  return headers;
}

function assertNotScreenLocked(endpoint: string): void {
  if (!screenLockGate.isLocked) return;
  const allow =
    endpoint.includes("/security/pin/verify") ||
    endpoint.includes("/security/pin/forgot") ||
    endpoint.includes("/auth/logout");
  if (!allow) {
    throw new Error("Экран заблокирован. Разблокируйте PIN для продолжения.");
  }
}

/**
 * Обработка ответа от API
 */
async function handleResponse<T>(response: Response, url?: string): Promise<T> {
  // Обработка 401 - автоматический logout
  if (response.status === 401) {
    removeToken();
    // Можно добавить редирект на страницу логина
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  // Обработка других ошибок
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    
    // Проверяем, является ли это кадастровым эндпоинтом с 404 (это нормальная ситуация)
    const isCadastral404 = response.status === 404 && url && (
      url.includes('/cadastral') || 
      url.includes('/cadastral/detailed')
    );
    
    // Проверяем, является ли это service-integrations с 502 (может быть временная недоступность)
    const isServiceIntegration502 = response.status === 502 && url && (
      url.includes('/service-integrations')
    );
    
    // Клонируем response для чтения body, так как его можно прочитать только один раз
    const responseClone = response.clone();
    let apiError: ApiClientError | null = null;
    try {
      const errorData: ApiError = await responseClone.json();
      errorMessage = messageFromFastApiDetail(errorData.detail, errorMessage);
      apiError = new ApiClientError(errorMessage, errorData.detail);

      // Для кадастровых 404 не логируем как ошибку, только как debug
      if (isCadastral404) {
        console.debug('[API] Cadastral data not found (expected):', url);
      } else if (isServiceIntegration502) {
        // Для 502 service-integrations логируем как warning, не как error
        console.warn('[API] Service integrations temporarily unavailable:', url);
      } else {
        console.error('[API Error]', response.status, errorData);
        console.error('[API Error Details]', JSON.stringify(errorData, null, 2));
        
        // Отправляем ошибку в Sentry (если настроен)
        try {
          const { captureException } = await import("@/lib/sentry");
          captureException(new Error(`API Error ${response.status}: ${errorMessage}`), {
            url,
            status: response.status,
            errorData,
          });
        } catch {
          // Sentry не настроен или не загружен - игнорируем
        }
      }

      if (
        Array.isArray(errorData.detail) &&
        !isCadastral404 &&
        !isServiceIntegration502
      ) {
        console.error("[API Error Validation]", errorMessage);
      }
    } catch {
      // Если не удалось распарсить JSON, используем текстовое сообщение
      try {
        const text = await response.text();
        if (isServiceIntegration502) {
          console.warn('[API] Service integrations temporarily unavailable:', url);
        } else if (!isCadastral404) {
          console.error('[API Error]', response.status, text);
        }
        errorMessage = text || errorMessage;
      } catch {
        // Если и text() не работает, используем дефолтное сообщение
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    }
    
    throw apiError ?? new Error(errorMessage);
  }

  // Для 204 No Content возвращаем пустой объект
  if (response.status === 204) {
    return {} as T;
  }

  // Парсим JSON
  try {
    return await response.json();
  } catch {
    return {} as T;
  }
}

/**
 * Универсальная функция для API запросов
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    data?: any;
    includeAuth?: boolean;
    params?: Record<string, string | number | boolean>;
  } = {}
): Promise<T> {
  const { 
    method = 'GET', 
    data, 
    includeAuth = true,
    params 
  } = options;

  // Формируем URL с query параметрами
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  assertNotScreenLocked(endpoint);

  if (url.includes('/work-items/map')) {
    console.log('[API] слой карточек work items →', method, url);
  } else {
    console.log(`[API] ${method} ${url}`, data ? 'with data' : '');
  }

  const noStore =
    (method === 'GET' &&
      (url.includes('/work-items/map') ||
        url.includes('/telegram/incidents/map') ||
        url.includes('/geometries'))) ||
    url.includes('/security/2fa/');

  const response = await fetch(url, {
    method,
    headers: createHeaders(includeAuth),
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'same-origin',
    cache: noStore ? 'no-store' : 'default',
  });

  return handleResponse<T>(response, url);
}

/**
 * GET запрос
 */
export async function get<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET', params });
}

/**
 * POST запрос
 */
export async function post<T>(
  endpoint: string,
  data?: any
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'POST', data });
}

/**
 * PUT запрос
 */
export async function put<T>(
  endpoint: string,
  data?: any
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'PUT', data });
}

/**
 * DELETE запрос
 */
export async function del<T = void>(
  endpoint: string
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
}

/**
 * PATCH запрос
 */
export async function patch<T>(
  endpoint: string,
  data?: any
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'PATCH', data });
}

/**
 * POST запрос с FormData (для загрузки файлов)
 */
export async function postFormData<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  console.log(`[API] POST ${url} (multipart/form-data)`);
  
  const headers: HeadersInit = {
    'Accept': 'application/json',
  };
  
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'same-origin',
  });
  
  return handleResponse<T>(response, url);
}

/**
 * GET запрос для скачивания файла
 */
export async function downloadFile(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): Promise<Blob> {
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  console.log(`[API] GET ${url} (download)`);
  
  const headers: HeadersInit = {};
  
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'same-origin',
  });
  
  // Обработка ошибок
  if (response.status === 401) {
    removeToken();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }
  
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ApiError = await response.json();
      if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      }
    } catch {
      // Игнорируем ошибки парсинга
    }
    throw new Error(errorMessage);
  }
  
  return response.blob();
}

